import { useState } from 'react';
import { Check, Palette } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { useTheme } from '@/contexts/ThemeContext';

const HINT_KEY = 'bodymax-theme-picker-used';

export function ThemeSelector({ className = '', hint = false, testId = 'theme-selector-btn' }) {
  const { accentTheme, setAccentTheme, themes } = useTheme();
  const [open, setOpen] = useState(false);
  const [glow, setGlow] = useState(() => hint && !localStorage.getItem(HINT_KEY));

  const handleOpenChange = (next) => {
    setOpen(next);
    if (next && glow) {
      localStorage.setItem(HINT_KEY, '1');
      setGlow(false);
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <button
          className={`flex items-center justify-center rounded-full active:scale-95 transition-transform ${glow ? 'theme-picker-hint' : ''} ${className}`}
          aria-label="Change theme"
          data-testid={testId}
        >
          <Palette size={18} className="text-accent" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[236px] p-3" data-testid="theme-selector-menu">
        <p className="px-0.5 pb-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
          Theme
        </p>
        <div className="grid grid-cols-2 gap-2.5">
          {themes.map(t => {
            const active = t.id === accentTheme;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => { setAccentTheme(t.id); setOpen(false); }}
                aria-pressed={active}
                data-testid={`theme-option-${t.id}`}
                className="relative block rounded-xl overflow-hidden border-2 active:scale-95 transition-transform"
                style={{
                  borderColor: active ? t.accentHex : 'rgba(255,255,255,0.10)',
                  boxShadow: active ? `0 0 14px ${t.accentHex}55` : 'none',
                }}
              >
                <img
                  src={t.thumb}
                  alt={`${t.label} theme`}
                  loading="lazy"
                  draggable={false}
                  className="w-full h-[108px] object-cover pointer-events-none"
                />
                {active && (
                  <span
                    className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full flex items-center justify-center"
                    style={{ background: t.accentHex, color: t.onAccent }}
                    data-testid={`theme-active-${t.id}`}
                  >
                    <Check size={12} strokeWidth={3.5} />
                  </span>
                )}
                <span
                  className="absolute bottom-0 left-0 right-0 py-1 text-[10px] font-semibold text-white text-center"
                  style={{ background: 'rgba(0,0,0,0.62)' }}
                >
                  {t.label}
                </span>
              </button>
            );
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
